import { useId, useMemo, useState, useCallback } from 'react';
import useCommonStore from '@/stores/common';
import { useUpdateParticipantMutation } from '@/services';
import toast from 'react-hot-toast';
import { toHHmm } from '@/utils/common';

const srOnly = 'sr-only';
const headerCell =
  'px-4 py-2 bg-blue-600 text-white text-xs md:text-sm font-semibold text-center border border-white';
const valueCell =
  'px-4 py-3 text-gray-800 text-xs md:text-sm bg-gray-50 border border-gray-200';

const UpdateParticipantModal = () => {
  const { modalConfig, hideCommonModal } = useCommonStore();
  const lesson = (modalConfig?.data?.lesson || {}) as any;
  const headingId = useId();
  const descriptionId = useId();

  const [clientName, setClientName] = useState<string>(
    lesson?.client_name ?? ''
  );
  const [partyCount, setPartyCount] = useState<number>(
    typeof lesson?.number_in_party === 'number'
      ? (lesson?.number_in_party ?? 0)
      : Number(lesson?.number_in_party) || 0
  );
  const [cc, setCc] = useState<'cash' | 'commission'>(
    (lesson?.payment_method as 'cash' | 'commission') ?? 'commission'
  );

  const partyOptions = useMemo(
    () => Array.from({ length: 11 }, (_, i) => i + 1),
    []
  );

  const { mutateAsync: updateParticipant, isPending: isUpdating } =
    useUpdateParticipantMutation();

  const handleUpdate = useCallback(async () => {
    try {
      const payload = {
        client_name: (clientName ?? '').trim() || '',
        lesson_code_id: lesson?.lesson_code?.id ?? lesson?.lesson_code_id ?? 0,
        number_in_party: Number(partyCount || 0),
        total_gross: Number(lesson?.total_gross ?? 0),
        commission: Number(lesson?.commission ?? 0),
        total_net: Number(lesson?.total_net ?? 0),
        level: Number(lesson?.level ?? 0),
        start: toHHmm(lesson?.start),
        mountain_id: Number(lesson?.mountain?.id ?? lesson?.mountain_id ?? 0),
        payment_method: cc
      } as const;
      const res = await updateParticipant({
        id: lesson?.id,
        ...payload
      });
      if ((res as any)?.success || (res as any)?.status === 200) {
        toast.success('Participant updated successfully');
        if (typeof modalConfig?.data?.refetch === 'function') {
          modalConfig.data.refetch();
        }
        hideCommonModal();
      }
    } catch {
      // handled globally
    }
  }, [cc, clientName, hideCommonModal, lesson, partyCount, updateParticipant]);
  return (
    <div
      className='p-4 md:p-6'
      role='dialog'
      aria-modal='true'
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
    >
      <h2
        id={headingId}
        className='text-lg md:text-xl font-semibold text-gray-900 mb-4'
      >
        Update Participant
      </h2>
      <p id={descriptionId} className={srOnly}>
        Review participant details such as client, counts, and financials.
      </p>

      <div className='w-full overflow-x-auto'>
        <div className='min-w-[720px]'>
          <div className='grid grid-cols-12' role='grid' aria-readonly='true'>
            <div className={`col-span-3 ${headerCell}`}>Client</div>
            <div className={`col-span-3 ${headerCell}`}>Instructor</div>
            <div className={`col-span-3 ${headerCell}`}>Hotel</div>
            <div className={`col-span-3 ${headerCell}`}>Agent</div>

            <div className={`col-span-3 ${valueCell}`}>
              <div>
                <label htmlFor='client_name' className={srOnly}>
                  Client name
                </label>
                <input
                  id='client_name'
                  type='text'
                  className='w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  value={clientName}
                  onChange={e => setClientName(e.target.value)}
                />
              </div>
            </div>
            <div className={`col-span-3 ${valueCell}`}>
              {lesson?.financial?.schedule_lessons?.instructor?.name ?? 'N/A'}
            </div>
            <div className={`col-span-3 ${valueCell}`}>
              {lesson?.hotel?.name ?? '-'}
            </div>
            <div className={`col-span-3 ${valueCell}`}>
              {lesson?.agent?.name ?? '-'}
            </div>

            <div className={`col-span-3 ${headerCell}`}>Service</div>
            <div className={`col-span-3 ${headerCell}`}>CC</div>
            <div className={`col-span-3 ${headerCell}`}>Gross</div>
            <div className={`col-span-3 ${headerCell}`}>Level</div>

            <div className={`col-span-3 ${valueCell}`}>
              {lesson?.lesson_code?.code ?? lesson?.service ?? '-'}
            </div>
            <div className={`col-span-3 ${valueCell}`}>
              <div>
                <label htmlFor='cc_method' className={srOnly}>
                  Payment method
                </label>
                <select
                  id='cc_method'
                  className='w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  value={cc}
                  onChange={e => setCc(e.target.value as 'cash' | 'commission')}
                >
                  <option value='cash'>Cash</option>
                  <option value='commission'>Commission</option>
                </select>
              </div>
            </div>
            <div className={`col-span-3 ${valueCell}`}>
              {lesson?.total_gross ?? '-'}
            </div>
            <div className={`col-span-3 ${valueCell}`}>
              {lesson?.financial?.schedule_lessons?.level ?? '-'}
            </div>

            <div className={`col-span-3 ${headerCell}`}>Party</div>
            <div className={`col-span-3 ${headerCell}`}>Net</div>
            <div className={`col-span-3 ${headerCell}`}></div>
            <div className={`col-span-3 ${headerCell}`}></div>

            <div className={`col-span-3 ${valueCell}`}>
              <div>
                <label htmlFor='party' className={srOnly}>
                  Party size
                </label>
                <select
                  id='party'
                  className='w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
                  value={Number(partyCount)}
                  onChange={e => setPartyCount(Number(e.target.value))}
                >
                  {partyOptions.map(n => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={`col-span-3 ${valueCell}`}>
              {lesson?.total_net ?? '-'}
            </div>
            <div className={`col-span-3 ${valueCell}`}></div>
            <div className={`col-span-3 ${valueCell}`}></div>
          </div>
        </div>
      </div>

      <div className='mt-6 flex justify-end gap-3'>
        <button
          type='button'
          className='px-4 py-2 bg-gray-100 text-slate-700 rounded-md hover:bg-gray-200'
          onClick={hideCommonModal}
          aria-label='Close modal'
        >
          Close
        </button>
        <button
          type='button'
          className='px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-60'
          onClick={handleUpdate}
          disabled={isUpdating}
          aria-label='Update participant'
        >
          {isUpdating ? 'Updating...' : 'Update'}
        </button>
      </div>
    </div>
  );
};

export default UpdateParticipantModal;
